import { PROPERTY_STATUS_META } from "../../realty/lib/propertyLifecycle";

/**
 * Write/action availability — fail-closed on corporate_properties_available.
 * Inventory READ for ACTIVE members is independent of this gate (A3I/A3U).
 */
export function isCorporatePropertiesAvailable(company) {
  return company?.corporate_properties_available === true;
}

/** Alias: corporate Property WRITE availability (create/edit/lifecycle/publication actions). */
export function canUseCompanyPropertyWrites(company) {
  return isCorporatePropertiesAvailable(company);
}

export const CATALOG_PUBLICATION_LABELS = {
  unpublished: "Не опубліковано",
  published: "Опубліковано в каталозі",
};

export function catalogPublicationLabel(value) {
  if (!value) return CATALOG_PUBLICATION_LABELS.unpublished;
  return CATALOG_PUBLICATION_LABELS[value] || value || "—";
}

/**
 * Compact location label for list/overview consumers.
 * Prefers formatted_address, then admin_labels, then settlement chain.
 */
export function safeSettlementLocationLabel(location) {
  if (!location || typeof location !== "object") return "—";
  const formatted = location.formatted_address;
  if (formatted && String(formatted).trim()) {
    return String(formatted).trim();
  }
  if (Array.isArray(location.admin_labels) && location.admin_labels.length) {
    const fromAdmin = location.admin_labels
      .map((item) =>
        item && typeof item === "object"
          ? String(item.name || "").trim()
          : String(item || "").trim(),
      )
      .filter(Boolean);
    if (fromAdmin.length) return fromAdmin.join(", ");
  }
  const parts = [
    location.settlement_name,
    location.region_name,
    location.district_name,
    location.country_name,
  ].filter((part) => part && String(part).trim());
  if (parts.length) return parts.join(", ");
  return "—";
}

export function coverImageUrl(property) {
  const cover = property?.cover_image;
  if (!cover) return null;
  if (typeof cover === "string" && cover.trim()) return cover.trim();
  if (typeof cover === "object") {
    return (
      cover.url ||
      cover.image ||
      cover.image_url ||
      cover.thumbnail ||
      cover.thumbnail_url ||
      null
    );
  }
  return null;
}

export function safeGalleryImages(property) {
  const images = property?.images;
  if (!Array.isArray(images)) return [];
  return images.filter((img) => {
    const kind = img?.kind;
    return kind === "image" || kind === "plan" || kind == null;
  });
}

export const PROPERTY_TYPE_LABELS = {
  apartment: "Квартира",
  house: "Будинок",
  land: "Земля",
  commercial: "Комерція",
  industrial: "Промисловість",
};

export const DEAL_TYPE_LABELS = {
  sale: "Продаж",
  rent: "Оренда",
};

export const propertyTypeLabel = (type) =>
  PROPERTY_TYPE_LABELS[type] || type || "—";

export const dealTypeLabel = (type) => DEAL_TYPE_LABELS[type] || type || "—";

export const propertyStatusLabel = (status) =>
  PROPERTY_STATUS_META[status]?.label || status || "—";

export function propertyDisplayTitle(property) {
  if (!property) return "—";
  if (property.title && String(property.title).trim()) {
    return String(property.title).trim();
  }
  return `Об'єкт #${property.id}`;
}

export function formatPropertyPrice(property) {
  if (!property || property.price == null || property.price === "") {
    return "—";
  }
  const currency = property.currency || "";
  return `${property.price}${currency ? ` ${currency}` : ""}`;
}

/**
 * TeamSafe / personal identity label — never email.
 * Prefer display_name, then name parts, then handle, then Користувач #id.
 */
export function userDisplayName(user) {
  if (!user) return "—";
  const displayName =
    typeof user.display_name === "string" ? user.display_name.trim() : "";
  if (displayName) return displayName;
  const fullname =
    typeof user.fullname === "string" ? user.fullname.trim() : "";
  if (fullname) return fullname;
  const parts = [user.first_name, user.last_name]
    .filter((part) => part && String(part).trim())
    .map((part) => String(part).trim());
  if (parts.length) return parts.join(" ");
  const handle = typeof user.handle === "string" ? user.handle.trim() : "";
  if (handle) return handle;
  if (user.id != null) return `Користувач #${user.id}`;
  return "—";
}

/** Confidential keys redacted by D1 when capability is absent. */
export const CONFIDENTIAL_PROPERTY_KEYS = [
  "description_private",
  "cooperation_offer_enabled",
  "cooperation_compensation_type",
  "cooperation_compensation_value",
  "cooperation_compensation_currency",
  "cooperation_terms",
];

export function hasRecordKey(property, key) {
  if (!property || !key) return false;
  return Object.prototype.hasOwnProperty.call(property, key);
}

export function hasConfidentialKey(property, key) {
  return hasRecordKey(property, key);
}

export function hasConfidentialProjection(property) {
  return CONFIDENTIAL_PROPERTY_KEYS.some((key) =>
    hasConfidentialKey(property, key),
  );
}

/** True when at least one subtype payload is present (record safe_read). */
export function hasRenderableSubtype(property) {
  if (!property || typeof property !== "object") return false;
  return Boolean(property.residential || property.land || property.commercial);
}

/**
 * Adapt corporate media projection for DetailGallery (expects `image`).
 * Accepts inventory/detail rows that may expose `url` instead of `image`.
 */
export function toGalleryProperty(property) {
  if (!property || typeof property !== "object") return property;
  const images = Array.isArray(property.images)
    ? property.images.map((img) => {
        if (!img || typeof img !== "object") return img;
        const image =
          img.image ||
          img.url ||
          img.image_url ||
          img.thumbnail ||
          img.thumbnail_url ||
          null;
        return { ...img, image };
      })
    : property.images;
  return {
    ...property,
    images,
  };
}

/**
 * Map flat corporate cooperation_* fields → nested cooperation_offer
 * for DetailCooperationBlock. Null when confidential projection absent.
 */
export function toCooperationOfferProperty(property) {
  if (!hasConfidentialProjection(property)) return null;
  if (
    property.cooperation_offer != null &&
    typeof property.cooperation_offer === "object"
  ) {
    return property;
  }
  return {
    ...property,
    cooperation_offer: {
      enabled: Boolean(property.cooperation_offer_enabled),
      type: property.cooperation_compensation_type || "",
      value:
        property.cooperation_compensation_value === undefined
          ? null
          : property.cooperation_compensation_value,
      currency: property.cooperation_compensation_currency || "",
      terms: property.cooperation_terms || "",
    },
  };
}

const SAFE_CORE_KEYS = [
  "title",
  "description_public",
  "video_url",
  "property_type",
  "deal_type",
  "price",
  "currency",
  "has_recovery_program",
  "has_housing_program",
];

/**
 * Build PATCH body for corporate Property core edit.
 * Never invent owner/company/created_by/responsible_user.
 * Never send confidential keys omitted by the server.
 */
export function buildCorporatePropertyPatchBody(baseline, values) {
  const body = {};
  for (const key of SAFE_CORE_KEYS) {
    const next = values[key];
    const prev = baseline?.[key];
    if (String(next ?? "") !== String(prev ?? "")) {
      if (typeof next === "string") {
        body[key] = next.trim();
      } else if (typeof next === "boolean") {
        body[key] = next;
      } else if (next === "" || next == null) {
        body[key] = next === "" ? "" : null;
      } else {
        body[key] = next;
      }
    }
  }
  if (hasConfidentialKey(baseline, "description_private")) {
    const next = values.description_private ?? "";
    const prev = baseline?.description_private ?? "";
    if (String(next) !== String(prev)) {
      body.description_private =
        typeof next === "string" ? next.trim() : next;
    }
  }
  return body;
}

export function emptyCoreFormFromProperty(property) {
  const form = {
    title: property?.title || "",
    description_public: property?.description_public || "",
    video_url: property?.video_url || "",
    property_type: property?.property_type || "apartment",
    deal_type: property?.deal_type || "sale",
    price: property?.price ?? "",
    currency: property?.currency || "USD",
    has_recovery_program: Boolean(property?.has_recovery_program),
    has_housing_program: Boolean(property?.has_housing_program),
  };
  if (hasConfidentialKey(property, "description_private")) {
    form.description_private = property.description_private || "";
  }
  return form;
}

export function cooperationFormFromProperty(property) {
  if (!hasConfidentialProjection(property)) return null;
  return {
    cooperation_offer_enabled: Boolean(property.cooperation_offer_enabled),
    cooperation_compensation_type:
      property.cooperation_compensation_type || "",
    cooperation_compensation_value:
      property.cooperation_compensation_value ?? "",
    cooperation_compensation_currency:
      property.cooperation_compensation_currency || "USD",
    cooperation_terms: property.cooperation_terms || "",
  };
}
